import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Circle, ChevronDown, ChevronUp, ArrowRight, X, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetupChecklist = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.dealer_id || user.dealer_id === 'default') return;
    // Hide checklist for demo dealer
    if (user?.email === 'demo@autohub360.ca') { setDismissed(true); return; }
    const hidden = sessionStorage.getItem('setup_checklist_hidden');
    if (hidden === 'true') { setDismissed(true); return; }
    fetchProgress();
  }, [user]);

  const fetchProgress = async () => {
    try {
      const res = await fetch(`${API}/setup-progress/${user.dealer_id}`);
      if (res.ok) {
        const d = await res.json();
        if (d.percentage < 100) setData(d);
      }
    } catch (e) { /* silent */ }
  };

  const dismiss = () => {
    sessionStorage.setItem('setup_checklist_hidden', 'true');
    setDismissed(true);
  };

  if (dismissed || !data) return null;

  const { percentage, completed, total, checks } = data;
  const items = Object.entries(checks);

  return (
    <div
      className="mx-6 mb-6 rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 via-slate-800/60 to-slate-900/80 backdrop-blur-xl overflow-hidden transition-all duration-500"
      data-testid="setup-checklist"
    >
      {/* Header — always visible */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
        data-testid="setup-checklist-header"
      >
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15.5" fill="none"
                stroke={percentage >= 80 ? '#10b981' : percentage >= 40 ? '#2BAEF5' : '#FF8A3B'}
                strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${percentage * 0.9738} 97.38`}
                className="transition-all duration-700"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">
              {percentage}%
            </span>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Configuration de votre espace</h3>
            <p className="text-[11px] text-slate-400">{completed} sur {total} etapes completees</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            className="p-1 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors"
            data-testid="setup-checklist-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5">
        <div className="h-1 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${percentage}%`,
              background: percentage >= 80 ? '#10b981' : percentage >= 40 ? 'linear-gradient(90deg, #2BAEF5, #60a5fa)' : 'linear-gradient(90deg, #FF8A3B, #f59e0b)',
            }}
          />
        </div>
      </div>

      {/* Expandable checklist */}
      {expanded && (
        <div className="px-5 pb-4 pt-3 space-y-1" data-testid="setup-checklist-items">
          {items.map(([key, item]) => (
            <button
              key={key}
              onClick={() => !item.done && navigate(item.link)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 group ${
                item.done
                  ? 'bg-transparent'
                  : 'hover:bg-white/5 cursor-pointer'
              }`}
              data-testid={`setup-item-${key}`}
              disabled={item.done}
            >
              {item.done ? (
                <CheckCircle2 className="w-4.5 h-4.5 text-emerald-400 flex-shrink-0" />
              ) : (
                <Circle className="w-4.5 h-4.5 text-slate-500 flex-shrink-0 group-hover:text-[#2BAEF5] transition-colors" />
              )}
              <div className="flex-1 min-w-0">
                <span className={`text-sm font-medium block ${item.done ? 'text-slate-500 line-through' : 'text-slate-200 group-hover:text-white'}`}>
                  {item.label}
                </span>
                <span className="text-[11px] text-slate-500 block truncate">{item.detail}</span>
              </div>
              {!item.done && (
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-[#2BAEF5] transition-colors flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SetupChecklist;
